import { Project } from "product-test-types";

import { TestAppState } from "../../app";
import { FilterData } from "../../components/project-filter-dialog";

/**
 * this is used to keep the field order consistent across the app, independent of how the backend sends the data or how the user reorders columns in the UI. The order here is based on the most common use cases and can be adjusted as needed. The getOverviewFieldOrderIndex function maps field names to their index in this order, and is used to sort headers and maintain column order in the projects table.
 */
export const OVERVIEW_TABLE_FIELD_ORDER = [
  "customId",
  "name",
  "owner",
  "category",
  "status",
  "degreeOfCompletion",
  "progress",
  "startDate",
  "endDate",
  "updateStatus",
] as const;

// Map a field name to its canonical column index used by row data arrays.
export const getOverviewFieldOrderIndex = (fieldName: string): number => {
  const index = OVERVIEW_TABLE_FIELD_ORDER.indexOf(
    fieldName as (typeof OVERVIEW_TABLE_FIELD_ORDER)[number]
  );
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

// Apply display order safely, keeping all headers even if the order is stale.
export const applyColumnOrderToHeaders = <T>(
  headers: T[],
  colOrder?: number[]
): T[] => {
  if (!colOrder || colOrder.length === 0) {
    return headers;
  }

  const validOrdered = colOrder
    .filter(
      (index) => Number.isInteger(index) && index >= 0 && index < headers.length
    )
    .map((index) => headers[index]);

  if (validOrdered.length === headers.length) {
    return validOrdered;
  }

  const seen = new Set(validOrdered);
  const missing = headers.filter((header) => !seen.has(header));
  return [...validOrdered, ...missing];
};

// Rebuild UI column order from persisted header sequence (by field name).
export const getColumnOrderFromHeaders = (
  headers: { fieldName: string }[] | undefined
): number[] | undefined => {
  if (!headers || headers.length === 0) {
    return undefined;
  }

  const baseIndexByFieldName = new Map<string, number>(
    OVERVIEW_TABLE_FIELD_ORDER.map((fieldName, index) => [fieldName, index])
  );

  const ordered = headers
    .map((header) => baseIndexByFieldName.get(header.fieldName))
    .filter((index): index is number => index !== undefined);

  if (ordered.length !== OVERVIEW_TABLE_FIELD_ORDER.length) {
    return undefined;
  }

  return ordered;
};

// Keep render inputs in canonical order; PMTable applies user order via colOrder.
export const sortHeadersByBaseFieldOrder = <T extends { fieldName: string }>(
  headers: T[]
): T[] => {
  return [...headers].sort(
    (a, b) =>
      getOverviewFieldOrderIndex(a.fieldName) -
      getOverviewFieldOrderIndex(b.fieldName)
  );
};

export const doesProjectPassFilterCriteria = (
  project: Project,
  filterData: FilterData | undefined,
  settings: TestAppState["settings"] | undefined
): boolean => {
  if (!filterData) {
    return true;
  }

  const selectedStatuses = (filterData.statusFilter ?? [])
    .map((v) => v.trim())
    .filter(Boolean);
  const selectedCompletions = (filterData.completionFilter ?? [])
    .map((v) => v.trim())
    .filter(Boolean);
  const selectedCategories = (filterData.categoryFilter ?? [])
    .map((v) => v.trim())
    .filter(Boolean);

  const searchQuery = (filterData.searchQueryFilter ?? []).join(" ").trim();

  const statusForDisplay =
    settings?.configuration?.statusReportingLevel === "category"
      ? (project?._calculated?.overallSemaphoreStatus?.name ?? "")
      : (project?._calculated?.overallStatus?.name ?? "");

  const statusCandidates = [
    statusForDisplay,
    project?._calculated?.overallStatus?.name ?? "",
  ]
    .map((v) => v.trim())
    .filter(Boolean);

  if (
    selectedStatuses.length > 0 &&
    !statusCandidates.some((status) => selectedStatuses.includes(status))
  ) {
    return false;
  }

  const completionValue = (
    project?._calculated?.overallDegreeOfCompletion?.name ?? ""
  ).trim();
  if (
    selectedCompletions.length > 0 &&
    !selectedCompletions.includes(completionValue)
  ) {
    return false;
  }

  const categoryValue = (project?.category ?? "").trim();
  if (
    selectedCategories.length > 0 &&
    !selectedCategories.includes(categoryValue)
  ) {
    return false;
  }

  const normalizedQuery = searchQuery.toLowerCase();
  if (normalizedQuery.length > 0) {
    const name = (project?.name ?? "").toLowerCase();
    const customId = (project?.customId?.toString() ?? "").toLowerCase();
    const owner = (project?.owner ?? "").toLowerCase();

    const matches =
      name.includes(normalizedQuery) ||
      customId.includes(normalizedQuery) ||
      owner.includes(normalizedQuery);

    if (!matches) {
      return false;
    }
  }

  return true;
};
